import { useEffect, useMemo, useRef, useState } from "react";

const markerDefinitions = [
  ["cueInFrame", "CUE IN", "#55aaff"],
  ["introFrame", "INTRO", "#ffd04d"],
  ["hookInFrame", "HOOK IN", "#f39034"],
  ["hookOutFrame", "HOOK OUT", "#ff5252"],
  ["outroFrame", "OUTRO", "#d458ff"],
  ["mixFrame", "MIX", "#43df87"],
  ["cueOutFrame", "CUE OUT", "#8aa0b8"],
];

function secondsFor(playout, key) {
  const rate = Number(playout?.sampleRate ?? 48_000);
  return Math.max(0, Number(playout?.[key] ?? 0) / rate);
}

function clock(value) {
  const seconds = Math.max(0, Number(value) || 0);
  const whole = Math.floor(seconds);
  return `${String(Math.floor(whole / 60)).padStart(2, "0")}:${String(whole % 60).padStart(2, "0")}.${Math.floor((seconds % 1) * 10)}`;
}

export default function AudioMarkerEditor({ asset, stationID, onMarkerChange }) {
  const audioRef = useRef(null);
  const segmentEndRef = useRef(null);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(Number(asset?.durationSeconds ?? 0));
  const [playing, setPlaying] = useState(false);
  const [activeSegment, setActiveSegment] = useState("");
  const [error, setError] = useState("");
  const source = asset?.content?.sha256 && stationID
    ? `/api/v1/media/${asset.content.sha256}?stationID=${encodeURIComponent(stationID)}`
    : "";
  const maximum = Math.max(1, duration || Number(asset?.durationSeconds) || secondsFor(asset?.playout, "cueOutFrame"));
  const segments = useMemo(() => {
    const playout = asset?.playout;
    const cueIn = secondsFor(playout, "cueInFrame");
    const cueOut = Math.max(cueIn, secondsFor(playout, "cueOutFrame") || maximum);
    const intro = secondsFor(playout, "introFrame");
    const hookIn = secondsFor(playout, "hookInFrame");
    const hookOut = secondsFor(playout, "hookOutFrame");
    const outro = secondsFor(playout, "outroFrame");
    return [
      { id:"full", label:"CELÝ ZVUK", start:cueIn, end:cueOut },
      { id:"intro", label:"INTRO", start:cueIn, end:intro },
      { id:"hook", label:"HOOK", start:hookIn, end:hookOut },
      { id:"outro", label:"OUTRO", start:outro, end:cueOut },
    ].filter((segment) => Number.isFinite(segment.start) && Number.isFinite(segment.end) && segment.end > segment.start);
  }, [asset, maximum]);

  useEffect(() => {
    setPosition(0);
    setDuration(Number(asset?.durationSeconds ?? 0));
    setPlaying(false);
    setActiveSegment("");
    setError("");
    segmentEndRef.current = null;
  }, [asset?.id]);

  async function playSegment(segment) {
    const audio = audioRef.current;
    if (!audio) return;
    setError("");
    if (playing && activeSegment === segment.id) {
      audio.pause();
      segmentEndRef.current = null;
      return;
    }
    audio.currentTime = Math.min(segment.start, Number.isFinite(audio.duration) ? audio.duration : segment.start);
    segmentEndRef.current = segment.end;
    setActiveSegment(segment.id);
    try { await audio.play(); }
    catch (failure) { setError(failure?.message || "Prohlížeč zvuk nepřehrál."); }
  }

  function seek(next) {
    const audio = audioRef.current;
    const value = Math.max(0, Math.min(maximum, Number(next)));
    if (audio) audio.currentTime = value;
    segmentEndRef.current = null;
    setActiveSegment("");
    setPosition(value);
  }

  return <fieldset className="mix-marker-card audio-marker-editor">
    <legend>Poslech a mixovací markery</legend>
    <audio
      ref={audioRef}
      src={source}
      preload="metadata"
      controls
      onLoadedMetadata={(event) => setDuration(Number(event.currentTarget.duration) || Number(asset?.durationSeconds ?? 0))}
      onPlay={() => setPlaying(true)}
      onPause={() => setPlaying(false)}
      onEnded={() => { setPlaying(false); setActiveSegment(""); segmentEndRef.current = null; }}
      onTimeUpdate={(event) => {
        const current = event.currentTarget.currentTime;
        setPosition(current);
        if (segmentEndRef.current != null && current >= segmentEndRef.current) {
          event.currentTarget.pause();
          segmentEndRef.current = null;
          setActiveSegment("");
        }
      }}
      onError={() => setError("Audio server odmítl nebo prohlížeč nedokázal načíst zvuk.")}
    />
    <div className="audio-segment-buttons" aria-label="Poslech tagů skladby">
      {segments.map((segment) => <button type="button" key={segment.id} className={playing && activeSegment === segment.id ? "active" : ""} onClick={() => playSegment(segment)}>{playing && activeSegment === segment.id ? "■" : "▶"} {segment.label}</button>)}
    </div>
    <label className="audio-scrubber"><span><b>POZICE</b><output>{clock(position)} / {clock(maximum)}</output></span><input aria-label="Posunout přehrávání" type="range" min="0" max={maximum} step="0.01" value={Math.min(position, maximum)} onChange={(event) => seek(event.target.value)} /></label>
    {error ? <p className="warning-copy" role="alert">{error}</p> : null}
    <div className="marker-drag-list">
      {markerDefinitions.map(([key, label, color]) => {
        const value = Math.min(maximum, secondsFor(asset?.playout, key));
        return <label key={key} style={{ "--marker-color":color }}><span><b>{label}</b><output>{clock(value)}</output></span><input aria-label={`Posunout ${label}`} type="range" min="0" max={maximum} step="0.01" value={value} onChange={(event) => onMarkerChange(key, event.target.value)} /><input aria-label={`${label} v sekundách`} type="number" min="0" max={maximum} step="0.01" value={value.toFixed(2)} onChange={(event) => onMarkerChange(key, event.target.value)} /></label>;
      })}
    </div>
  </fieldset>;
}
